import React, { useState } from 'react';
import type { Candidate } from '@axiom/validation';
import {
  Button,
  Card,
  CardHeader,
  CardSection,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  IconButton,
  ImageCircle,
  LargeHeader,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  useBreakpoint,
} from '@axiom/ui';
import {
  CandidatesConst,
  CountryCodesConst,
  DisplayPronouns,
  PossibleImageSizes,
} from '@axiom/const';

import { CandidateUtil } from '../../utils/candidate-util';
import { ProfileImageEdit } from '../ProfileImageEdit/ProfileImageEdit';
import { CompensationModal } from '../CompensationModal/CompensationModal';

import { ProfileHeaderProfileImageBanner } from './ProfileHeaderProfileImageBanner';
import { ProfileHeaderBarAdmissions } from './ProfileHeaderBarAdmissions';
import { ProfileHeaderDisplayNameForm } from './ProfileHeaderDisplayNameForm';

export type ProfileHeaderProps = {
  candidate: Candidate;
  name?: string;
};

const ValidCompProfileStatusSet = new Set([
  CandidatesConst.ProfileStatuses.Active,
  CandidatesConst.ProfileStatuses.Alum,
  CandidatesConst.ProfileStatuses.AlumDNR,
  CandidatesConst.ProfileStatuses.Beach,
  CandidatesConst.ProfileStatuses.Idle,
  CandidatesConst.ProfileStatuses.PendingActive,
  CandidatesConst.ProfileStatuses.PendingAlum,
  CandidatesConst.ProfileStatuses.PendingAlumDNR,
  CandidatesConst.ProfileStatuses.PendingBeach,
  CandidatesConst.ProfileStatuses.Waitlist,
]);
const ValidCompCountyCodesSet = new Set([
  CountryCodesConst.CountryCodesAbbreviations.AU,
  CountryCodesConst.CountryCodesAbbreviations.CA,
  CountryCodesConst.CountryCodesAbbreviations.HK,
  CountryCodesConst.CountryCodesAbbreviations.SG,
  CountryCodesConst.CountryCodesAbbreviations.UK,
  CountryCodesConst.CountryCodesAbbreviations.US,
]);

export const ProfileHeader = ({
  candidate,
  name = 'PROFILE_HEADER',
}: ProfileHeaderProps) => {
  const [isEditShowing, setIsEditShowing] = useState(false);
  const { isMobile, isSmallScreen } = useBreakpoint();
  const [viewCompModal, setViewCompModal] = useState(false);

  const canViewCompensation = () => {
    return (
      candidate.employeeType !== CandidatesConst.Badges.Contractor &&
      ValidCompProfileStatusSet.has(candidate.profileStatus) &&
      ValidCompCountyCodesSet.has(candidate.homeOffice?.countryCode)
    );
  };

  return (
    <>
      <Card name={name}>
        <CardHeader>
          <Layout position="middle" wrap>
            <LayoutItem rightGutter="16px">
              <ImageCircle
                name="PROFILE_IMAGE_NAME"
                background="blue"
                imageName={candidate.calculatedDisplayName}
                src={CandidateUtil.getProfileImageUri(
                  candidate,
                  PossibleImageSizes.W_160
                )}
              />
            </LayoutItem>
            <LayoutItem fluid>
              <LargeHeader name="CALCULATED_DISPLAY_NAME">
                {candidate.calculatedDisplayName}
              </LargeHeader>
              {candidate.pronouns ? (
                <Gutter bottom="8px">
                  <Paragraph name="PRONOUNS">
                    {DisplayPronouns[candidate.pronouns]}
                  </Paragraph>
                </Gutter>
              ) : null}
            </LayoutItem>
            {!isEditShowing && (
              <LayoutItem>
                <IconButton
                  name="EDIT_BUTTON"
                  icon="pencil"
                  pattern="secondary"
                  variation="minimal"
                  onClick={() => {
                    setIsEditShowing(true);
                  }}
                />
              </LayoutItem>
            )}
          </Layout>
        </CardHeader>
        {isEditShowing ? (
          <CardSection name="EDIT_SECTION" divider>
            <ProfileHeaderProfileImageBanner
              candidate={candidate}
              name="PROFILE_HEADER_SUGGEST_PHOTO_HINT_EDIT_MODE"
            />
            <ParagraphHeader name="PENDO-PROFILE-PHOTO">
              Profile Photo
            </ParagraphHeader>
            <Gutter bottom="16px" />
            <ProfileImageEdit />
            <Gutter bottom="24px" />
            <ProfileHeaderDisplayNameForm
              candidate={candidate}
              name="PROFILE_HEADER_DISPLAY_NAME_FORM"
              onCancel={() => {
                setIsEditShowing(false);
              }}
              onSuccess={() => {
                setIsEditShowing(false);
              }}
            />
          </CardSection>
        ) : null}
        <CardSection>
          {!isEditShowing && (
            <ProfileHeaderProfileImageBanner
              name="PROFILE_HEADER_SUGGEST_PHOTO_HINT_INITIAL"
              candidate={candidate}
              dismissible
              onAdd={() => {
                setIsEditShowing(true);
              }}
            />
          )}
          <Grid>
            <GridRow gutterBottom="16px">
              <GridColumn>
                <ProfileHeaderBarAdmissions candidate={candidate} />
              </GridColumn>
            </GridRow>
            <GridRow>
              <GridColumn>
                <Layout stackableOn="mobile">
                  <LayoutItem
                    rightGutter={isSmallScreen ? '0px' : '18px'}
                    bottomGutter={isSmallScreen ? '18px' : '0px'}
                  >
                    <Button
                      to="./client-view"
                      variation="outline"
                      pattern="secondary"
                      name="PENDO_TALENT_PROFILE_CLIENT_VIEW_BUTTON"
                      fluid={isMobile}
                    >
                      Client View
                    </Button>
                  </LayoutItem>
                  {canViewCompensation() ? (
                    <LayoutItem>
                      <Button
                        variation="outline"
                        pattern="secondary"
                        name="VIEW_COMPENSATION"
                        fluid={isMobile}
                        onClick={() => {
                          setViewCompModal(prevState => !prevState);
                        }}
                      >
                        View Compensation
                      </Button>
                    </LayoutItem>
                  ) : null}
                </Layout>
              </GridColumn>
            </GridRow>
          </Grid>
        </CardSection>
      </Card>
      {viewCompModal ? (
        <CompensationModal
          candidate={candidate}
          openCloseModal={() => {
            setViewCompModal(prevState => !prevState);
          }}
        />
      ) : null}
    </>
  );
};
